import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { GENERIC_SECTION_INSUFFICIENT_MESSAGE } from '../../../../lib/sectionFallbacks/derivedFallbacks';
import { EditorialTimeline, EditorialRecommendations, EditorialBadge } from '../../report/editorial';
import { makeRenderCitedText, emptyStateNoteProps } from './feasibilityBodyHelpers';
import { isEditorialLayoutsEnabled } from '../../../../lib/runtimeFlags';
import {
  parseActionPlanPhaseDescription,
  parseRecommendationForCard,
  reportHasStructuredActionPlanDetail,
} from '../../../../lib/reportGeneration/actionPlanPresentation';

/**
 * #34 P2 STAGE B — Action Plan & Development Pathway (feasibility). Lifted
 * VERBATIM from the SiteRagReportView inline block (body only; router shell owns
 * the chrome). This is feasibility's RICH actionPlan — the phased
 * EditorialTimeline + the numbered EditorialRecommendations — NOT the generic
 * `ActionPlanSection` PowerAvailability stub. The router keys this component on
 * `reportType === 'feasibility'` (capacityCreditDeepDive / actionPlan are shared
 * ids across feasibility/power/water/environmental, so the override must be
 * type-keyed, never bare-id).
 *
 * SECTION-PORT (17A "numbered list + conditions", phased timeline for the
 * phase-tagged developmentPathway). We do NOT invent phase assignments for the
 * flat recommendations.
 */
export const FeasibilityActionPlanSection: React.FC<{ report: SiteRagReport; label?: string }> = ({ report }) => {
  const theme = useEffectiveReportTheme();
  const renderCitedText = makeRenderCitedText(report);
  const hasStructuredDetail = reportHasStructuredActionPlanDetail(
    report.developmentPathway,
    report.recommendations,
  );

  return !(report.developmentPathway?.length || report.recommendations?.length) ? (
    // N1.5C-flat — graceful-omit note flattens to a muted left-rule note on the
    // cream page in editorial mode (shared seam); flag OFF byte-identical.
    <div {...emptyStateNoteProps(isEditorialLayoutsEnabled(), theme, false)} data-testid="actionPlan-empty">
      {GENERIC_SECTION_INSUFFICIENT_MESSAGE}
    </div>
    // N1 — in the editorial monograph, keep the timeline + recommendations grid
    // atomic (gated) so it never splits mid-row across a page (a fragmented CSS
    // grid desynced its column tracks). Spans both N2 flow columns via
    // `data-span-columns`; flag-OFF is byte-stable.
  ) : (
    <div
      data-span-columns
      style={isEditorialLayoutsEnabled() && !hasStructuredDetail ? { breakInside: 'avoid' } : undefined}
      className="grid grid-cols-1 lg:grid-cols-5 gap-8"
    >
      <div className="lg:col-span-3">
        <EditorialTimeline
          theme={theme}
          variant="phased"
          // DEFECT 3 — phase head = the DESCRIPTIVE title (e.g. "Diligence &
          // options", "Phase 1 BTM (50 MW)"); the bare `phase.phase` ordinal
          // ("0"/"1"/"2") collided visually with the "01/02" action step
          // numbers (duplicate numbering). The right-aligned `window` now
          // carries the COD when present (else omitted) — never the title,
          // which is already the phase head.
          phases={(report.developmentPathway ?? []).map((phase) => {
            const hasDescription = Boolean(phase.description?.trim());
            const parsed = hasStructuredDetail
              ? parseActionPlanPhaseDescription(phase.description)
              : null;

            if (!parsed || !parsed.hasStructuredDisclosure) {
              return {
                label: hasDescription ? phase.title : (phase.phase || phase.title),
                window: hasDescription ? (phase.cod || undefined) : (phase.cod || phase.capacityMW || undefined),
                actions: [renderCitedText(hasDescription ? phase.description : (phase.title || phase.capacityMW || phase.phase))],
              };
            }

            return {
              label: hasDescription ? phase.title : (phase.phase || phase.title),
              window: hasDescription ? (phase.cod || undefined) : (phase.cod || phase.capacityMW || undefined),
              actions: [
                <div key="concise-phase-card" className="space-y-3" data-testid="concise-phase-action-card">
                  {parsed.leadNarrative && (
                    <div>{renderCitedText(parsed.leadNarrative)}</div>
                  )}
                  {parsed.waterCases.map((waterCase, caseIdx) => (
                    <div
                      key={caseIdx}
                      className="rounded border border-slate-200/80 bg-white/60 p-2.5 text-xs"
                      data-testid="phase-water-summary-table-card"
                      style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}
                    >
                      <div
                        className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2"
                        style={{ breakAfter: 'avoid', pageBreakAfter: 'avoid' }}
                      >
                        <strong style={{ color: roleColor(theme, 'ink') }}>
                          Modeled Phase Water Screening — {waterCase.scope}
                        </strong>
                        {waterCase.sourceRefs && (
                          <span className="text-[11px]" style={{ color: roleColor(theme, 'muted') }}>
                            {renderCitedText(waterCase.sourceRefs)}
                          </span>
                        )}
                      </div>
                      {(waterCase.loadConversion || waterCase.effectiveThermalLoad) && (
                        <p className="mb-2 text-[11px]" style={{ color: roleColor(theme, 'muted') }}>
                          {[waterCase.loadConversion, waterCase.effectiveThermalLoad].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      <table className="w-full border-collapse text-left text-[11px]" data-sleek-table="true">
                        <thead>
                          <tr className="border-b border-slate-300">
                            <th scope="col" className="py-1 pr-2 font-semibold">Scenario</th>
                            <th scope="col" className="py-1 pr-2 font-semibold">Boundary &amp; Flow</th>
                            <th scope="col" className="py-1 pr-2 font-semibold">Average Day</th>
                            <th scope="col" className="py-1 pr-2 font-semibold">Annual</th>
                            <th scope="col" className="py-1 pr-2 font-semibold">100%-Load / Peak Assist</th>
                            <th scope="col" className="py-1 font-semibold" style={{ minWidth: 72 }}>Refs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {waterCase.rows.map((row, rowIdx) => (
                            <tr key={rowIdx} className="border-b border-slate-200/70 align-top">
                              <th scope="row" className="py-1 pr-2 font-medium" style={{ color: roleColor(theme, 'ink') }}>
                                {row.label}
                              </th>
                              <td className="py-1 pr-2" style={{ color: roleColor(theme, 'muted') }}>
                                {row.boundary}; {row.flowBasis}
                              </td>
                              <td className="py-1 pr-2">
                                {row.averageDay ?? (row.conditionalNote ? '0 gal/d (verified dry mode only)' : '—')}
                              </td>
                              <td className="py-1 pr-2">{row.annual ?? '—'}</td>
                              <td className="py-1 pr-2">
                                {row.assistSummary
                                  ? `Adiabatic trim: ${row.assistSummary}`
                                  : row.fullLoad ?? row.conditionalNote ?? '—'}
                              </td>
                              <td className="py-1 break-words" style={{ minWidth: 72 }}>
                                {row.refs ? renderCitedText(row.refs) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="mt-1.5 text-[10.5px]" style={{ color: roleColor(theme, 'muted') }}>
                        Non-additive screening alternatives (data-center water only; excludes power-plant process water and utility commitment gates). Full scenario caveats and parameters are retained in the Appendix.
                      </p>
                    </div>
                  ))}
                  {parsed.staffing && (
                    <div
                      className="rounded border border-slate-200/80 bg-white/60 px-2.5 py-1.5 text-xs"
                      data-testid="phase-staffing-summary-card"
                    >
                      <strong style={{ color: roleColor(theme, 'ink') }}>Modeled Phase Staffing: </strong>
                      <span>{parsed.staffing.estimates.join(' ')}</span>{' '}
                      {parsed.staffing.sourceRefs && renderCitedText(parsed.staffing.sourceRefs)}
                    </div>
                  )}
                  {parsed.supportingDisclosureText && (
                    <details className="print:hidden text-xs" data-testid="phase-supporting-detail-toggle">
                      <summary className="cursor-pointer font-medium" style={{ color: roleColor(theme, 'accent') }}>
                        Expand full phase water &amp; staffing methodology, assumptions &amp; caveats
                      </summary>
                      <div className="mt-1.5 whitespace-pre-line rounded border border-slate-200/70 bg-white/50 p-2.5 text-xs">
                        {renderCitedText(parsed.supportingDisclosureText)}
                      </div>
                    </details>
                  )}
                </div>,
              ],
            };
          })}
        />
      </div>
      <div className="lg:col-span-2">
        {/* 17A fallback — flat recommendations as a numbered list; the
            type badge composes inline with each title. */}
        <EditorialRecommendations
          theme={theme}
          heading="Key Recommendations"
          marker="decimal"
          items={(report.recommendations || []).map((rec, idx) => {
            const parsedRec = parseRecommendationForCard(rec, hasStructuredDetail, {
              siteId: report.siteId,
              reportId: report.id,
              sedId: report.sedId,
              references: report.references,
              developmentPathway: report.developmentPathway,
            });
            const summaryText = parsedRec.summaryDescription || rec.description || '';
            if (!parsedRec.hasSupportingDetail && parsedRec.structuredMeta.length === 0) {
              return (
                <span key={idx}>
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ color: roleColor(theme, 'ink') }}>{rec.title}</strong>
                    <EditorialBadge theme={theme} variant="outline" tone={rec.type === 'power' ? 'warning' : rec.type === 'cooling' ? 'accent' : 'positive'}>{rec.type}</EditorialBadge>
                  </span>
                  {summaryText && (
                    <span style={{ display: 'block', marginTop: 4 }}>{renderCitedText(summaryText)}</span>
                  )}
                </span>
              );
            }

            return (
              <span key={idx} data-testid="concise-recommendation-card">
                <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ color: roleColor(theme, 'ink') }}>{rec.title}</strong>
                  <EditorialBadge theme={theme} variant="outline" tone={rec.type === 'power' ? 'warning' : rec.type === 'cooling' ? 'accent' : 'positive'}>{rec.type}</EditorialBadge>
                </span>
                {parsedRec.structuredMeta.length > 0 && (
                  <span style={{ display: 'block', marginTop: 3, fontSize: '0.85em', color: roleColor(theme, 'muted') }}>
                    {parsedRec.structuredMeta.map((m) => `${m.label}: ${m.value}`).join(' · ')}
                  </span>
                )}
                {summaryText && (
                  <span style={{ display: 'block', marginTop: 4 }}>
                    {renderCitedText(summaryText)}
                  </span>
                )}
                {parsedRec.supportingDetail && (
                  <details className="print:hidden mt-1.5 text-xs" data-testid="recommendation-supporting-detail-toggle">
                    <summary className="cursor-pointer font-medium" style={{ color: roleColor(theme, 'accent') }}>
                      Supporting rationale &amp; benchmark detail (retained in Appendix for PDF)
                    </summary>
                    <span style={{ display: 'block', marginTop: 4 }}>
                      {renderCitedText(parsedRec.supportingDetail)}
                    </span>
                  </details>
                )}
              </span>
            );
          })}
        />
      </div>
    </div>
  );
};
